import React, { useState } from 'react';
import {
    Package, LogOut, Search, Activity, Users, ShoppingCart,
    ChevronDown, ChevronRight, UserPlus, List
} from 'lucide-react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';

export default function MediStore() {
    const navigate = useNavigate();
    const location = useLocation();

    // State for Collapsible Menus
    const [openMenus, setOpenMenus] = useState({
        patients: true,
        inventory: true
    });

    const toggleMenu = (menu) => {
        setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
    };

    // Helper: Checks if the current URL matches the link
    const isActive = (path) => location.pathname === path;
    const isParentActive = (pathPart) => location.pathname.includes(pathPart);

    return (
        <div className="fixed inset-0 bg-[#F8FAFC] flex font-sans antialiased text-slate-900 overflow-hidden">

            {/* --- SIDEBAR --- */}
            <aside className="w-72 bg-[#0F172A] text-slate-300 flex flex-col shadow-2xl z-20">
                <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
                    {/* Brand */}
                    <div className="flex items-center gap-3 mb-10 cursor-pointer" onClick={() => navigate('/store')}>
                        <div className="bg-emerald-500 p-2 rounded-xl shadow-lg shadow-emerald-500/20">
                            <Package size={24} className="text-white" />
                        </div>
                        <div>
                            <span className="text-xl font-bold text-white block leading-none tracking-tight">Boseth</span>
                            <span className="text-emerald-500 text-[10px] uppercase font-bold tracking-widest">Internal System</span>
                        </div>
                    </div>

                    <nav className="space-y-2">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 px-3">Management</p>

                        {/* Inventory Section */}
                        <div>
                            <div
                                onClick={() => toggleMenu('inventory')}
                                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer hover:bg-slate-800 transition-all group ${isParentActive('/store') && !location.pathname.includes('patient') ? 'bg-emerald-500/10 text-emerald-400' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <Activity size={20} />
                                    <span className="font-medium">Inventory</span>
                                </div>
                                {openMenus.inventory ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                            </div>
                            {openMenus.inventory && (
                                <div className="ml-9 mt-2 space-y-1 border-l border-slate-700/50 pl-4">
                                    <Link to="/store" className={`block py-2 text-sm transition ${isActive('/store') ? 'text-emerald-400 font-bold' : 'hover:text-emerald-400'}`}>
                                        Stock List
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Patients Section */}
                        <div>
                            <div
                                onClick={() => toggleMenu('patients')}
                                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer hover:bg-slate-800 transition-all group ${isParentActive('patient') ? 'bg-emerald-500/10 text-emerald-400' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <Users size={20} />
                                    <span className="font-medium">Patients</span>
                                </div>
                                {openMenus.patients ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                            </div>
                            {openMenus.patients && (
                                <div className="ml-9 mt-2 space-y-1 border-l border-slate-700/50 pl-4">
                                    <Link to="/store/patients-list" className={`flex items-center gap-2 py-2 text-sm transition ${isActive('/store/patients-list') ? 'text-emerald-400 font-bold' : 'hover:text-emerald-400'}`}>
                                        <List size={14}/> Patients List
                                    </Link>
                                    <Link to="/store/new-patient" className={`flex items-center gap-2 py-2 text-sm transition ${isActive('/store/new-patient') ? 'text-emerald-400 font-bold' : 'hover:text-emerald-400'}`}>
                                        <UserPlus size={14}/> Add Patient
                                    </Link>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3 p-3 hover:bg-slate-800 rounded-xl cursor-pointer transition-all">
                            <ShoppingCart size={20}/> <span>Orders</span>
                        </div>
                    </nav>
                </div>

                {/* Status & Logout */}
                <div className="p-8 bg-[#0B1222]">
                    <div className="bg-slate-800/40 p-4 rounded-2xl border border-slate-700/30 mb-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-emerald-400 uppercase mb-1">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                            MYSQL LIVE
                        </div>
                        <p className="text-[11px] text-slate-400 text-nowrap">Database connected</p>
                    </div>
                    <button onClick={() => navigate('/')} className="w-full p-3 bg-slate-800 hover:bg-red-500/10 hover:text-red-400 text-slate-400 rounded-xl flex items-center justify-center gap-2 transition-all font-bold">
                        <LogOut size={18}/> Logout
                    </button>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <main className="flex-1 overflow-y-auto">
                <header className="sticky top-0 z-10 bg-[#F8FAFC]/80 backdrop-blur-md px-10 py-6 flex justify-end items-center gap-4">
                    <div className="relative group">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        <input type="text" placeholder="Search dashboard..." className="bg-white border border-slate-200 rounded-2xl py-2.5 pl-11 pr-4 w-64 text-sm outline-none focus:ring-4 focus:ring-emerald-500/5 shadow-sm" />
                    </div>
                    <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                        AD
                    </div>
                </header>

                <div className="px-10 pb-10">
                    {/* This is where InventoryList, PatientsList, or NewPatient will load */}
                    <Outlet />
                </div>
            </main>
        </div>
    );
}